import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MessageSquareIcon, SendIcon } from 'lucide-react';
import { Message } from '../types';
interface MessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  messages: Message[];
  jobTitle: string;
  customerName: string;
}
export function MessagesModal({
  isOpen,
  onClose,
  messages,
  jobTitle,
  customerName
}: MessagesModalProps) {
  const [messageInput, setMessageInput] = useState('');
  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
    } else if (diffDays === 1) {
      return `Yesterday ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })}`;
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit'
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  };
  const handleSendMessage = () => {
    if (!messageInput.trim()) return;
    // Placeholder for sending message
    console.log('Sending message:', messageInput);
    setMessageInput('');
  };
  return <AnimatePresence data-id="element-145">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" data-id="element-146" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-id="element-147">
            <motion.div initial={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 100,
          scale: 0.95
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg h-[600px] overflow-hidden flex flex-col" data-id="element-148">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 flex items-center justify-between" data-id="element-149">
                <div className="flex items-center gap-3 flex-1" data-id="element-150">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-151">
                    <span className="text-white font-bold text-lg" data-id="element-152">
                      {customerName.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-153">
                    <div className="flex items-center gap-2 mb-1" data-id="element-154">
                      <MessageSquareIcon className="w-5 h-5" data-id="element-155" />
                      <h2 className="text-xl font-bold truncate" data-id="element-156">
                        {customerName}
                      </h2>
                    </div>
                    <p className="text-slate-300 text-sm truncate" data-id="element-157">
                      {jobTitle}
                    </p>
                  </div>
                </div>
                <motion.button whileTap={{
              scale: 0.9
            }} onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-158">
                  <XIcon className="w-6 h-6" data-id="element-159" />
                </motion.button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-id="element-160">
                {messages.length === 0 ? <div className="flex items-center justify-center h-full" data-id="element-161">
                    <div className="text-center py-12" data-id="element-162">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-163">
                        <MessageSquareIcon className="w-8 h-8 text-slate-400" data-id="element-164" />
                      </div>
                      <p className="text-slate-600 font-semibold mb-1" data-id="element-165">
                        No messages yet
                      </p>
                      <p className="text-slate-500 text-sm" data-id="element-166">
                        Start a conversation with {customerName}
                      </p>
                    </div>
                  </div> : messages.map((message, index) => <motion.div key={message.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className={`flex ${message.isFromCustomer ? 'justify-start' : 'justify-end'}`} data-id="element-167">
                      <div className={`max-w-[75%] ${message.isFromCustomer ? 'order-1' : 'order-2'}`} data-id="element-168">
                        <div className={`rounded-2xl px-4 py-2 ${message.isFromCustomer ? 'bg-white text-slate-900 rounded-tl-sm shadow-sm' : 'bg-orange-600 text-white rounded-tr-sm'}`} data-id="element-169">
                          <p className="text-sm leading-relaxed" data-id="element-170">
                            {message.text}
                          </p>
                        </div>
                        <p className={`text-xs text-slate-500 mt-1 px-1 ${message.isFromCustomer ? 'text-left' : 'text-right'}`} data-id="element-171">
                          {formatMessageTime(message.timestamp)}
                        </p>
                      </div>
                    </motion.div>)}
              </div>

              {/* Input */}
              <div className="p-4 bg-white border-t-2 border-slate-100" data-id="element-172">
                <div className="flex items-center gap-2" data-id="element-173">
                  <input type="text" value={messageInput} onChange={e => setMessageInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSendMessage()} placeholder={`Message ${customerName}...`} className="flex-1 px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-500 transition-colors" data-id="element-174" />
                  <motion.button whileTap={{
                scale: 0.9
              }} onClick={handleSendMessage} disabled={!messageInput.trim()} className="p-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-175">
                    <SendIcon className="w-5 h-5" data-id="element-176" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}